import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Switch,
    message,
    Space,
    Tag,
    Card,
    Popconfirm,
    Typography,
    Tooltip,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    StarOutlined,
    StarFilled,
    SyncOutlined,
} from '@ant-design/icons';
import currencyService from '../../services/currencyService';
import { useTranslation } from '../../../shared/i18n';
import I18nFieldTabs, { parseI18n, cleanI18n, mergeI18n } from '../../components/I18nFieldTabs';

const { Text } = Typography;

const Currencies = () => {
    const { t } = useTranslation();
    const [currencies, setCurrencies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCurrency, setEditingCurrency] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [ratesModalVisible, setRatesModalVisible] = useState(false);
    const [ratesSubmitting, setRatesSubmitting] = useState(false);
    const [ratesForm] = Form.useForm();

    useEffect(() => {
        loadCurrencies();
    }, []);

    const loadCurrencies = async () => {
        setLoading(true);
        try {
            const response = await currencyService.getCurrencies();
            if (response.success) {
                setCurrencies(response.data);
            } else {
                message.error(response.error || 'Failed to load currencies');
            }
        } catch (error) {
            message.error('Failed to load currencies: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingCurrency(null);
        form.resetFields();
        form.setFieldsValue({
            decimal_places: 2,
            exchange_rate: 1.0,
            enabled: true,
            sort_order: 0,
            i18n: {},
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingCurrency(record);
        form.setFieldsValue({
            ...record,
            code: record.code,
            i18n: parseI18n(record.i18n),
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const response = await currencyService.deleteCurrency(id);
            if (response.success) {
                message.success('Currency deleted');
                loadCurrencies();
            } else {
                message.error(response.error || 'Failed to delete currency');
            }
        } catch (error) {
            message.error('Failed to delete currency: ' + error.message);
        }
    };

    const handleSetDefault = async (id) => {
        try {
            const response = await currencyService.setDefaultCurrency(id);
            if (response.success) {
                message.success('Default currency updated');
                loadCurrencies();
            } else {
                message.error(response.error || 'Failed to set default currency');
            }
        } catch (error) {
            message.error('Failed to set default currency: ' + error.message);
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            let response;
            values = { ...values, i18n: cleanI18n(mergeI18n(editingCurrency?.i18n, values.i18n)) };
            if (editingCurrency) {
                response = await currencyService.updateCurrency({
                    id: editingCurrency.id,
                    ...values,
                });
            } else {
                response = await currencyService.createCurrency(values);
            }

            if (response.success) {
                message.success(editingCurrency ? 'Currency updated' : 'Currency created');
                setModalVisible(false);
                loadCurrencies();
            } else {
                message.error(response.error || 'Operation failed');
            }
        } catch (error) {
            message.error('Operation failed: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenRates = () => {
        const initial = {};
        currencies.forEach((c) => {
            initial[c.code] = c.exchange_rate != null ? Number(c.exchange_rate) : undefined;
        });
        ratesForm.setFieldsValue(initial);
        setRatesModalVisible(true);
    };

    const handleSubmitRates = async (values) => {
        const rates = {};
        Object.keys(values).forEach((code) => {
            if (values[code] != null && values[code] !== '') {
                rates[code] = values[code];
            }
        });
        if (Object.keys(rates).length === 0) {
            message.warning('No exchange rates to update');
            return;
        }
        setRatesSubmitting(true);
        try {
            const response = await currencyService.updateRates(rates);
            if (response.success) {
                message.success(response.message || 'Exchange rates updated');
                setRatesModalVisible(false);
                loadCurrencies();
            } else {
                message.error(response.error || 'Failed to update exchange rates');
            }
        } catch (error) {
            message.error('Failed to update exchange rates: ' + error.message);
        } finally {
            setRatesSubmitting(false);
        }
    };

    const handleToggleEnabled = async (record) => {
        try {
            const response = await currencyService.updateCurrency({
                id: record.id,
                enabled: !record.enabled,
            });
            if (response.success) {
                message.success(`Currency ${record.enabled ? 'disabled' : 'enabled'}`);
                loadCurrencies();
            } else {
                message.error(response.error || 'Failed to update currency');
            }
        } catch (error) {
            message.error('Failed to update currency: ' + error.message);
        }
    };

    const columns = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 80,
            render: (code, record) => (
                <Space>
                    <Text strong>{code}</Text>
                    {record.is_default && (
                        <Tag color="gold">Default</Tag>
                    )}
                </Space>
            ),
        },
        {
            title: 'Symbol',
            dataIndex: 'symbol',
            key: 'symbol',
            width: 80,
            align: 'center',
            render: (symbol) => <Text style={{ fontSize: 18 }}>{symbol}</Text>,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <div>
                    <div>{name}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.name_en}</Text>
                </div>
            ),
        },
        {
            title: 'Exchange Rate',
            dataIndex: 'exchange_rate',
            key: 'exchange_rate',
            width: 130,
            align: 'right',
            render: (rate) => rate != null ? Number(rate).toFixed(6) : '-',
        },
        {
            // 汇率超过 30 天未更新 → 余额支付会被拒绝（基准货币除外）
            title: 'Rate Updated',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 150,
            render: (t, record) => {
                if (!t) return '-';
                const stale = !record.is_default && (Date.now() - new Date(String(t).replace(' ', 'T')).getTime()) > 30 * 86400000;
                return <span style={stale ? { color: '#cf1322' } : undefined}>{String(t).slice(0, 16)}</span>;
            },
        },
        {
            title: 'Decimals',
            dataIndex: 'decimal_places',
            key: 'decimal_places',
            width: 90,
            align: 'center',
        },
        {
            title: 'Status',
            dataIndex: 'enabled',
            key: 'enabled',
            width: 100,
            align: 'center',
            render: (enabled, record) => (
                <Switch
                    checked={enabled}
                    onChange={() => handleToggleEnabled(record)}
                    disabled={record.is_default}
                />
            ),
        },
        {
            title: 'Sort',
            dataIndex: 'sort_order',
            key: 'sort_order',
            width: 70,
            align: 'center',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    {!record.is_default && (
                        <Tooltip title="Set as Default">
                            <Button
                                type="text"
                                icon={<StarOutlined />}
                                onClick={() => handleSetDefault(record.id)}
                            />
                        </Tooltip>
                    )}
                    {record.is_default && (
                        <Tooltip title="Default Currency">
                            <Button
                                type="text"
                                icon={<StarFilled style={{ color: '#faad14' }} />}
                                disabled
                            />
                        </Tooltip>
                    )}
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="Delete this currency?"
                        description="Make sure no products are using this currency."
                        onConfirm={() => handleDelete(record.id)}
                        okText="Delete"
                        cancelText="Cancel"
                        disabled={record.is_default}
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={record.is_default}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="货币管理"
            extra={
                <Space>
                    <Button icon={<SyncOutlined />} onClick={handleOpenRates}>
                        Update Rates
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Add Currency
                    </Button>
                </Space>
            }
        >
            <Table
                columns={columns}
                dataSource={currencies}
                rowKey="id"
                loading={loading}
                pagination={false}
                size="middle"
                scroll={{ x: 'max-content' }}
            />

            <Modal
                title={editingCurrency ? 'Edit Currency' : 'Add Currency'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="code"
                        label="Currency Code (ISO 4217)"
                        rules={[
                            { required: true, message: 'Please enter currency code' },
                            { len: 3, message: 'Currency code must be 3 characters' },
                        ]}
                    >
                        <Input
                            placeholder="USD"
                            maxLength={3}
                            style={{ textTransform: 'uppercase' }}
                            disabled={!!editingCurrency}
                        />
                    </Form.Item>

                    <Form.Item
                        name="symbol"
                        label="Symbol"
                        rules={[{ required: true, message: 'Please enter currency symbol' }]}
                    >
                        <Input placeholder="$" maxLength={10} />
                    </Form.Item>

                    <I18nFieldTabs
                        fields={[{ key: 'name', label: t('Name') }]}
                        base={
                            <Form.Item
                                name="name"
                                label="Name (Chinese)"
                                rules={[{ required: true, message: 'Please enter currency name' }]}
                            >
                                <Input placeholder="US Dollar (Chinese name)" />
                            </Form.Item>
                        }
                    />

                    <Form.Item
                        name="name_en"
                        label="Name (English)"
                        rules={[{ required: true, message: 'Please enter English name' }]}
                    >
                        <Input placeholder="US Dollar" />
                    </Form.Item>

                    <Form.Item
                        name="decimal_places"
                        label="Decimal Places"
                        rules={[{ required: true, message: 'Please enter decimal places' }]}
                    >
                        <InputNumber min={0} max={4} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="exchange_rate"
                        label="Exchange Rate (relative to USD)"
                        rules={[{ required: true, message: 'Please enter exchange rate' }]}
                        extra="1 USD = X of this currency"
                    >
                        <InputNumber
                            min={0.000001}
                            step={0.01}
                            precision={6}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="sort_order"
                        label="Sort Order"
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="enabled"
                        label="Enabled"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setModalVisible(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={submitting}>
                                {editingCurrency ? 'Update' : 'Create'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="批量更新汇率"
                open={ratesModalVisible}
                onCancel={() => setRatesModalVisible(false)}
                footer={null}
                width={500}
            >
                <Text type="secondary">
                    1 USD = X of each currency. Leave a field unchanged to keep its current rate.
                </Text>
                <Form
                    form={ratesForm}
                    layout="vertical"
                    onFinish={handleSubmitRates}
                    style={{ marginTop: 16 }}
                >
                    {currencies.map((c) => (
                        <Form.Item
                            key={c.code}
                            name={c.code}
                            label={`${c.code} - ${c.name_en || c.name}`}
                        >
                            <InputNumber
                                min={0.000001}
                                step={0.01}
                                precision={6}
                                style={{ width: '100%' }}
                                disabled={c.is_default}
                            />
                        </Form.Item>
                    ))}
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setRatesModalVisible(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={ratesSubmitting}>
                                Update Rates
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Currencies;
